import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { IonButton, IonCard, IonCardContent, IonIcon, IonInput, IonToggle } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { DecaDriverPlateStorageService, IDecaDriverPlateCriteria } from '../deca-driver-plate-storage.service';
import { IDecaFilters } from '../deca-store.model';
import { DecaStoreService } from '../deca-store.service';
import { DecaTodayListComponent } from '../deca-today-list/deca-today-list.component';

@Component({
  selector: 'lib-deca-driver-plate-selector',
  templateUrl: './deca-driver-plate-selector.component.html',
  styleUrls: ['./deca-driver-plate-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCard,
    ReactiveFormsModule,
    TranslateModule,
    IonButton,
    IonIcon,
    IonInput,
    IonToggle,
    DecaTodayListComponent,
    IonCardContent,
  ],
})
export class DecaDriverPlateSelectorComponent implements OnInit {
  public readonly detailRoute = input.required<string>();

  private readonly fb = inject(FormBuilder);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly plateStorageService = inject(DecaDriverPlateStorageService);

  protected readonly searchForm = this.fb.group({
    plate: [''],
  });

  protected readonly withoutPlate = signal(false);

  private readonly activeFilters = computed(() => this.decaStoreService.activeFiltersFor('today'));

  protected readonly hasSelection = computed(() => !!this.activeFilters());

  protected readonly isWithoutPlateSelected = computed(() => !!this.activeFilters()?.withoutPlate);

  protected readonly selectedPlate = computed(() => this.activeFilters()?.plate ?? '');

  public async ngOnInit(): Promise<void> {
    const activeFilters = this.decaStoreService.activeFiltersFor('today');
    if (activeFilters) {
      this.updateForm(activeFilters);
      return;
    }

    const storedCriteria = await this.plateStorageService.read();
    if (storedCriteria) {
      this.applyCriteria(storedCriteria, false);
    }
  }

  protected onWithoutPlateChange(checked: boolean): void {
    this.withoutPlate.set(checked);
    this.searchForm.controls.plate.setValue('');
  }

  protected selectPlate(): void {
    const withoutPlate = this.withoutPlate();
    const plate = (this.searchForm.controls.plate.value ?? '').trim();

    if (withoutPlate) {
      this.applyCriteria({ withoutPlate: true });
    } else if (plate) {
      this.applyCriteria({ plate });
    } else {
      this.applyCriteria({});
    }
  }

  protected changePlate(): void {
    this.updateForm(this.decaStoreService.activeFiltersFor('today') ?? {});
    this.decaStoreService.clearFilters('today');
  }

  private applyCriteria(criteria: IDecaDriverPlateCriteria, persist = true): void {
    if (criteria.withoutPlate) {
      this.decaStoreService.applyFilters({ withoutPlate: true }, 'today');
    } else if (criteria.plate) {
      this.decaStoreService.applyFilters({ plate: criteria.plate }, 'today');
    } else {
      this.decaStoreService.clearFilters('today');
    }

    this.updateForm(criteria);

    if (!persist) return;

    if (criteria.withoutPlate || criteria.plate) {
      void this.plateStorageService.save(criteria);
    } else {
      void this.plateStorageService.clear();
    }
  }

  private updateForm(criteria: IDecaFilters): void {
    this.withoutPlate.set(!!criteria.withoutPlate);
    this.searchForm.controls.plate.setValue(criteria.plate ?? '');
  }
}
